/**
 * ProductDetailPage — `/marketplace/products/:id`, rebuilt 2026-08 as the slip
 * the whole product is named after.
 *
 * This page is the end of the journey: the user has a product, and what they
 * need before walking into a bazaar is four facts — what it costs, whether it
 * is there, who has it, and which container to walk to. The shipped version had
 * all four but arranged them as a loose vertical list of same-weight blocks:
 * an 20px semibold price sitting next to a green pill, then a bordered card for
 * the seller, then a zebra-striped spec table. Nothing said which fact was the
 * answer.
 *
 * Now it is one ruled slip. The price is the instrument reading at 30px in
 * `.g-data` on `--g-figure` ink; availability is a chip beside it; the seller
 * and the stall are the next two rules down; and the spec table is drawn with
 * the same rules rather than with alternating fills (zebra striping is a
 * table-of-many-rows device — on five attributes it just adds two greys).
 *
 * Layout: photo and slip are side by side from `lg` up (the photo is real
 * stall photography and deserves the width), stacked below it. The container
 * widened from `max-w-3xl` to `max-w-5xl` so the two columns each get a usable
 * measure instead of splitting 768px.
 *
 * Public route (unauthenticated). Navigating here from an authed chat keeps the
 * chat thread mounted (module-global Zustand store); `navigate(-1)` returns to
 * the populated thread with the cards intact.
 *
 * States: `.g-skel` skeleton shaped like the real page while loading;
 * `role="alert"` + retry on error, plus a `notify.error` carrying the same
 * retry; never a blank screen. `<h1>` is the product name; focus moves to
 * `<main>` on mount; the description is plain text (`whitespace-pre-wrap`, NO
 * `dangerouslySetInnerHTML`). Tokens via the `--g-*` mirror; strings from the
 * `marketplace` namespace.
 */
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  BadgeCheck,
  Info,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Star,
  Store,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { ProductPhoto } from "@/features/marketplace/ProductPhoto";
import { useNavigate, useParams } from "react-router-dom";

import { iconProps } from "@/lib/icons";
import { MOTION } from "@/lib/motion";
import { notify } from "@/lib/notify";
import { useGardenMotion } from "@/lib/use-garden-motion";
import type {
  ProductAttribute,
  ProductDetailSeller,
  ProductVariant,
} from "@/features/marketplace/useProductDetail";

import { SellerLocationMap } from "@/features/marketplace/components/SellerLocationMap";
import { LoadError } from "@/features/marketplace/LoadError";
import {
  PriceFigure,
  sellerBrandName,
  StallLine,
  StockMark,
} from "@/features/marketplace/LabelGrid";
import {
  trackMapRevealed,
  trackProductViewed,
} from "@/features/marketplace/product-analytics";
import { useProductDetail } from "@/features/marketplace/useProductDetail";
import { formatPriceFigure } from "@/features/marketplace/price-format";
import type { KgsLocale } from "@/lib/format-currency";
import { kgsLocaleForLanguage } from "@/lib/format-currency";


/** The one chip shape this page uses for every trust signal. */
const TRUST_CHIP =
  "inline-flex items-center gap-1.5 rounded-full border border-[color:var(--g-rule)] px-2.5 py-1 text-xs font-medium text-[color:var(--g-ink-2)]";

export function ProductDetailPage(): ReactElement {
  const { id = "" } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation("marketplace");
  const navigate = useNavigate();
  const { reduced } = useGardenMotion();
  const locale = kgsLocaleForLanguage(i18n.language);
  const [showMap, setShowMap] = useState(false);
  const mainRef = useRef<HTMLElement | null>(null);

  const query = useProductDetail(id);
  const product = query.data;
  const { refetch } = query;

  // v0048 derivations. `price_max_kgs` is set only when a genuine span exists
  // — one priced SKU is an exact price, and prefixing «от» there would
  // understate what the buyer actually pays.
  //
  // v0052: the figure now renders BOTH ends («5 500 – 14 000 сом») rather than
  // the low one, so the «от» prefix is retired — "от 5 500 – 14 000" reads as
  // a floor under a band, which is not what the seller stated. The prefix
  // survives only in the i18n bundle for the variant summary elsewhere.
  const priceMax = toNumberOrNull(product?.price_max_kgs ?? null);
  // Contact the API actually served. A blank string is treated as absent: the
  // importer writes '' for a missing WhatsApp cell on some rows, and '' in a
  // `wa.me` link is a working link to nowhere.
  const sellerPhone = (product?.seller.phone_e164 ?? "").trim();
  const sellerWhatsapp = (product?.seller.whatsapp_phone_e164 ?? "").trim();
  const unitLabel =
    product?.unit != null && product.unit !== ""
      ? t(`unit.${product.unit}`, { defaultValue: product.unit })
      : null;
  const variants = [...(product?.variants ?? [])].sort(
    (a, b) => a.sort_order - b.sort_order,
  );
  const vocabulary = [...(product?.tags ?? []), ...(product?.folk_names ?? [])];
  const scalarRows = (
    [
      ["brand", product?.brand],
      ["article", product?.article],
      ["formalName", product?.formal_name],
    ] as const
  )
    .filter(([, value]) => value != null && value !== "")
    .map(([key, value]) => ({ key, value: String(value) }));

  const handleRetry = useCallback((): void => {
    void refetch();
  }, [refetch]);

  // Move focus to the main region on mount (screen-reader route-change a11y).
  useEffect(() => {
    mainRef.current?.focus();
  }, []);

  // `marketplace.product.viewed` — fires when the product resolves.
  useEffect(() => {
    if (product) trackProductViewed(product.id, product.seller_id);
  }, [product]);

  // A failed detail fetch names a recovery rather than leaving a dead end.
  useEffect(() => {
    if (!query.isError) return;
    notify.error(t("productDetail.error"), {
      id: "marketplace-product-detail-error",
      description: t("browse.loadErrorHint"),
      action: { label: t("productDetail.retry"), onClick: handleRetry },
    });
  }, [query.isError, t, handleRetry]);

  const revealMap = (): void => {
    setShowMap(true);
    if (product) trackMapRevealed(product.id, product.seller_id);
  };

  return (
    <main
      ref={mainRef}
      tabIndex={-1}
      className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-5 focus:outline-none sm:py-6"
      data-testid="product-detail-page"
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        data-testid="product-detail-back"
        className="inline-flex min-h-[44px] w-fit items-center gap-1.5 pr-2 text-sm font-medium text-[color:var(--g-ink-2)] transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <ArrowLeft {...iconProps("sm")} />
        {t("productDetail.back")}
      </button>

      {query.isPending ? (
        <DetailSkeleton />
      ) : query.isError || product === undefined ? (
        <LoadError
          message={t("productDetail.error")}
          onRetry={handleRetry}
          testId="product-detail-error"
          retryTestId="product-detail-retry"
          retryLabel={t("productDetail.retry")}
        />
      ) : (
        <article className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] lg:items-start lg:gap-8">
          {/* The photograph, framed and unretouched — WHOLE, and openable
              full-screen. It used to be cropped into this frame with
              `object-cover` and carry no click target at all (SCRUM-230). */}
          <ProductPhoto src={product.images[0]?.url} name={product.name} />

          <div className="flex min-w-0 flex-col gap-4">
            <h1 className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
              {product.name}
            </h1>

            {/* THE SLIP — price, seller, stall, in that order of importance. */}
            <div className="g-ruled overflow-hidden rounded-[var(--g-radius)] border border-[color:var(--g-rule)] bg-card text-card-foreground">
              <div
                data-testid="product-detail-price"
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-4"
              >
                <span className="flex items-baseline gap-1.5">
                  <PriceFigure
                    price={toNumberOrNull(product.price_kgs)}
                    priceMax={priceMax}
                    priceKind={product.price_kind ?? null}
                    size="detail"
                  />
                  {/* The unit qualifies the PRICE. «106 сом» per metre and per
                      piece are different offers, and the catalogue showed the
                      figure without the qualifier until v0048. */}
                  {unitLabel !== null ? (
                    <span className="text-sm font-medium text-[color:var(--g-ink-2)]">
                      /{unitLabel}
                    </span>
                  ) : null}
                </span>
                <StockMark state={product.stock_state} variant="chip" />
              </div>

              {product.bulk_price_kgs !== null &&
              product.bulk_threshold !== null ? (
                <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2.5 text-sm">
                  <span className="text-[color:var(--g-ink-2)]">
                    {t("card.bulkFrom", { count: product.bulk_threshold })}
                  </span>
                  <span className="g-data font-semibold text-[color:var(--g-figure)]">
                    {formatBulk(product.bulk_price_kgs, locale)}{" "}
                    <span className="font-body text-xs font-medium text-[color:var(--g-ink-2)]">
                      {t("card.currency")}
                    </span>
                  </span>
                </div>
              ) : null}

              {/* Who has it, and where to walk. */}
              <div className="flex flex-col gap-3 px-4 py-4">
                <h2 className="flex items-center gap-2 text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[color:var(--g-ink-2)]">
                  <Store {...iconProps("xs")} />
                  {t("productDetail.seller")}
                </h2>
                <p className="text-base font-semibold text-foreground">
                  {sellerBrandName(
                    product.seller.business_name,
                    product.seller.pavilion_number,
                  )}
                </p>
                <TrustSignals seller={product.seller} t={t} />
                <div className="flex flex-col gap-1">
                  <StallLine
                    market={product.market.name_ru}
                    pavilion={product.seller.pavilion_number}
                    className="text-xs"
                  />
                  <p className="text-xs text-[color:var(--g-ink-2)]">
                    {product.market.city} · {product.market.address}
                  </p>
                </div>

                {/* How to REACH them, above how to walk to them — a buyer
                    deciding on a 45 000-сом tool calls first and visits after.
                    Rendered only from what the API actually served: a minted
                    sentinel number is withheld server-side (v0053), and where
                    nothing reachable survives this block is absent entirely
                    rather than showing a dead control. */}
                {sellerPhone || sellerWhatsapp ? (
                  <div
                    data-testid="product-detail-contact"
                    className="mt-1 flex flex-wrap items-center gap-2"
                  >
                    {sellerPhone ? (
                      <a
                        href={`tel:${sellerPhone}`}
                        data-testid="product-detail-call"
                        aria-label={t("card.callAria", {
                          seller: product.seller.business_name,
                          phone: sellerPhone,
                        })}
                        className="g-data inline-flex min-h-[44px] items-center gap-2 rounded-[10px] border border-[color:var(--g-rule)] px-4 text-sm font-bold text-[color:var(--g-figure)] transition-colors hover:bg-[color:var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        <Phone {...iconProps("sm")} />
                        {sellerPhone}
                      </a>
                    ) : null}
                    {sellerWhatsapp ? (
                      <a
                        // wa.me wants digits only — a leading "+" is a dead link.
                        href={`https://wa.me/${sellerWhatsapp.replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="product-detail-whatsapp"
                        aria-label={t("card.whatsappAria", {
                          seller: product.seller.business_name,
                        })}
                        className="inline-flex min-h-[44px] items-center gap-2 rounded-[10px] border border-[color:var(--g-rule)] px-4 text-sm font-semibold text-foreground transition-colors hover:bg-[color:var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                      >
                        <MessageCircle {...iconProps("sm")} />
                        {t("card.whatsapp")}
                      </a>
                    ) : null}
                  </div>
                ) : null}

                {!showMap ? (
                  <button
                    type="button"
                    onClick={revealMap}
                    data-testid="product-detail-show-map"
                    className="mt-1 inline-flex min-h-[44px] w-fit items-center gap-2 rounded-[10px] bg-[color:var(--g-primary)] px-4 text-sm font-semibold text-[color:var(--g-on-primary)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <MapPin {...iconProps("sm")} />
                    {t("productDetail.showLocation")}
                  </button>
                ) : null}

                <AnimatePresence>
                  {showMap ? (
                    <motion.div
                      initial={reduced ? false : { opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={reduced ? undefined : { opacity: 0 }}
                      transition={{
                        duration: MOTION.dur.bubble,
                        ease: MOTION.ease.out,
                      }}
                      className="mt-1"
                    >
                      <SellerLocationMap
                        seller={product.seller}
                        market={product.market}
                      />
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>

            {/* A0: AI-fallibility microcopy near the parsed price — the
                assistant may surface a product, but the price and stock are the
                seller's, and this audience is here because they are afraid of
                being told the wrong number. */}
            <p
              data-testid="ai-disclaimer"
              className="flex items-start gap-1.5 text-xs leading-snug text-[color:var(--g-ink-2)]"
            >
              <Info {...iconProps("xs")} className="mt-0.5 shrink-0" />
              <span>{t("trust.aiDisclaimer")}</span>
            </p>

            {product.description !== null && product.description.length > 0 ? (
              <section className="flex flex-col gap-2">
                <h2 className="text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[color:var(--g-ink-2)]">
                  {t("productDetail.description")}
                </h2>
                <p className="max-w-prose whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                  {product.description}
                </p>
              </section>
            ) : null}

            <VariantTable variants={variants} locale={locale} t={t} />
            <VocabularyChips terms={vocabulary} t={t} />
            <SpecTable
              attributes={product.attributes ?? []}
              language={i18n.language}
              category={product.category_name_ru}
              scalars={scalarRows}
              t={t}
            />
          </div>
        </article>
      )}
    </main>
  );
}

/** Prices arrive as Decimal-serialized strings; coerce + guard before format. */
function toNumberOrNull(value: string | number | null): number | null {
  if (value === null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The bulk figure, or an em dash when the wire value will not coerce. */
function formatBulk(
  value: string | number,
  locale: ReturnType<typeof kgsLocaleForLanguage>,
): string {
  const n = toNumberOrNull(value);
  return n === null ? "—" : formatPriceFigure(n, locale);
}

/** Chosen-language text with RU fallback for a `{ru, ky, en}` triple. */
function pickTri(
  text: { ru: string; ky: string | null; en: string | null },
  language: string,
): string {
  const lang = language.split("-")[0];
  if (lang === "ky" && text.ky?.trim()) return text.ky;
  if (lang === "en" && text.en?.trim()) return text.en;
  return text.ru;
}

/**
 * Render one attribute's RAW display value (locked Q4): a `select` shows its
 * localized option label; `boolean` a localized yes/no; `size` the number +
 * raw unit; `date` a locale-formatted date; everything else the value as-is.
 * Returns `null` for a value that can't be shown (empty, or an `image`
 * attribute — its wire value is a bare upload id with no servable URL).
 */
function renderAttrValue(
  attr: ProductAttribute,
  language: string,
  t: (key: string) => string,
): string | null {
  if (attr.field_type === "image") return null; // no servable URL on the wire
  if (attr.value === null || attr.value === "") return null;

  switch (attr.field_type) {
    case "boolean":
      return attr.value === true ? t("productDetail.specYes") : t("productDetail.specNo");
    case "select":
      return attr.option_label ? pickTri(attr.option_label, language) : String(attr.value);
    case "size":
      return attr.unit ? `${String(attr.value)} ${attr.unit}` : String(attr.value);
    case "date": {
      const d = new Date(`${String(attr.value)}T00:00:00`);
      if (Number.isNaN(d.getTime())) return String(attr.value);
      const locale = language.startsWith("en") ? "en-GB" : "ru-RU";
      return d.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
    }
    default:
      return String(attr.value);
  }
}

/**
 * The variant table: one row per purchasable SKU.
 *
 * Drawn with the SAME rules as the spec table and deliberately NOT zebra
 * striped — striping is a table-of-many-rows device, and on nine sizes it just
 * adds greys. The design system indexes thirteen screens and product detail is
 * not one of them, so this stays inside the idiom the page already documents
 * rather than inventing a new one.
 *
 * Why it earns its place: 68 products carried a card price their own SKU list
 * contradicted — «Труба для водопровода» read 64 сом against nine sizes running
 * 106 to 3410. The «от» figure above is only honest if the buyer can see what
 * it is «от».
 *
 * Article and stock columns appear only when at least one row has them; the
 * two shipped sellers populate opposite halves (TEBO prices its SKUs, Dinqi
 * counts them), so a fixed four-column grid would be half empty either way.
 * Scrolls inside its own container — the page body never scrolls sideways.
 */
function VariantTable({
  variants,
  locale,
  t,
}: {
  variants: ProductVariant[];
  locale: KgsLocale;
  t: (key: string, opts?: Record<string, unknown>) => string;
}): ReactElement | null {
  if (variants.length === 0) return null;

  const anyArticle = variants.some((v) => v.article !== null && v.article !== "");
  const anyPrice = variants.some((v) => toNumberOrNull(v.price_kgs) !== null);
  const anyStock = variants.some((v) => v.stock_qty !== null);
  const dimension = variants[0]?.variant_attribute_key ?? t("productDetail.variantLabel");

  return (
    <section data-testid="product-detail-variants" className="flex flex-col gap-2">
      <h2 className="text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[color:var(--g-ink-2)]">
        {t("productDetail.variants", { count: variants.length })}
      </h2>
      <div className="overflow-x-auto rounded-[var(--g-radius)] border border-[color:var(--g-rule)]">
        <table className="g-ruled w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-[color:var(--g-ink-2)]">
              <th scope="col" className="px-4 py-2.5 font-medium">
                {dimension}
              </th>
              {anyArticle ? (
                <th scope="col" className="px-4 py-2.5 font-medium">
                  {t("productDetail.variantArticle")}
                </th>
              ) : null}
              {anyPrice ? (
                <th scope="col" className="px-4 py-2.5 text-right font-medium">
                  {t("productDetail.variantPrice")}
                </th>
              ) : null}
              {anyStock ? (
                <th scope="col" className="px-4 py-2.5 text-right font-medium">
                  {t("productDetail.variantStock")}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {variants.map((variant) => {
              const price = toNumberOrNull(variant.price_kgs);
              return (
                <tr key={variant.id} data-testid={`variant-row-${variant.id}`}>
                  <td className="px-4 py-2.5 font-medium text-foreground">
                    {variant.variant_attribute_value}
                  </td>
                  {anyArticle ? (
                    <td className="g-data px-4 py-2.5 text-[color:var(--g-ink-2)]">
                      {variant.article ?? "—"}
                    </td>
                  ) : null}
                  {anyPrice ? (
                    <td className="px-4 py-2.5 text-right">
                      {price === null ? (
                        <span className="text-[color:var(--g-ink-2)]">—</span>
                      ) : (
                        <span className="g-data font-semibold text-[color:var(--g-figure)]">
                          {formatPriceFigure(price, locale)}
                        </span>
                      )}
                    </td>
                  ) : null}
                  {anyStock ? (
                    <td className="g-data px-4 py-2.5 text-right text-[color:var(--g-ink-2)]">
                      {variant.stock_qty ?? "—"}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The seller's own words for this product — «Теги» and «Народные названия».
 *
 * 723 of these have been in the database since 2026-08-17, used as retrieval
 * evidence and shown to nobody. They are worth surfacing because they are how a
 * buyer would ASK for the thing («перт труба», «американка»), which is exactly
 * what a shopper who does not know the technical name needs to see confirmed.
 *
 * Rendered as plain chips, not links: they are not a taxonomy and clicking one
 * would promise a filtered view that does not exist.
 */
function VocabularyChips({
  terms,
  t,
}: {
  terms: string[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}): ReactElement | null {
  const unique = Array.from(new Set(terms.filter((term) => term.trim() !== "")));
  if (unique.length === 0) return null;

  return (
    <section data-testid="product-detail-vocabulary" className="flex flex-col gap-2">
      <h2 className="text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[color:var(--g-ink-2)]">
        {t("productDetail.alsoKnownAs")}
      </h2>
      <ul className="flex flex-wrap gap-1.5 p-0">
        {unique.map((term) => (
          <li
            key={term}
            className="inline-flex items-center rounded-full border border-[color:var(--g-rule)] px-2.5 py-1 text-xs text-[color:var(--g-ink-2)]"
          >
            {term}
          </li>
        ))}
      </ul>
    </section>
  );
}


/**
 * The spec table: every attribute with a displayable value, as ruled
 * "label : value" rows. A `size` or `date` value is a MEASUREMENT and takes the
 * instrument face; a select label or a yes/no is prose and does not. The
 * category joins the table as its first row rather than floating above the
 * heading as an eyebrow.
 */
function SpecTable({
  attributes,
  language,
  category,
  scalars,
  t,
}: {
  attributes: ProductAttribute[];
  language: string;
  category: string | null;
  /** v0048 one-to-one product facts, in the order a buyer scans for them.
   * `article` arrives already filtered by provenance — the API withholds one
   * it reconstructed rather than read. */
  scalars: { key: string; value: string }[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}): ReactElement | null {
  const rows = attributes
    .map((attr) => ({ attr, value: renderAttrValue(attr, language, t) }))
    .filter((r): r is { attr: ProductAttribute; value: string } => r.value !== null);

  if (rows.length === 0 && category === null && scalars.length === 0) return null;

  return (
    <section data-testid="product-detail-specs" className="flex flex-col gap-2">
      <h2 className="text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-[color:var(--g-ink-2)]">
        {t("productDetail.specs")}
      </h2>
      <dl className="g-ruled m-0 overflow-hidden rounded-[var(--g-radius)] border border-[color:var(--g-rule)]">
        {category !== null ? (
          <div className="flex items-start justify-between gap-4 px-4 py-2.5 text-sm">
            <dt className="text-[color:var(--g-ink-2)]">
              {t("productDetail.category")}
            </dt>
            <dd className="m-0 text-right font-medium text-foreground">
              {category}
            </dd>
          </div>
        ) : null}
        {scalars.map(({ key, value }) => (
          <div
            key={key}
            className="flex items-start justify-between gap-4 px-4 py-2.5 text-sm"
          >
            <dt className="text-[color:var(--g-ink-2)]">
              {t(`productDetail.${key}`)}
            </dt>
            <dd
              className={
                "m-0 text-right font-medium text-foreground " +
                (key === "article" ? "g-data" : "")
              }
            >
              {value}
            </dd>
          </div>
        ))}
        {rows.map(({ attr, value }) => {
          const isMeasurement =
            attr.field_type === "size" || attr.field_type === "date";
          return (
            <div
              key={attr.field_slug}
              className="flex items-start justify-between gap-4 px-4 py-2.5 text-sm"
            >
              <dt className="text-[color:var(--g-ink-2)]">
                {pickTri(attr.label, language)}
              </dt>
              <dd
                className={
                  "m-0 text-right font-medium text-foreground " +
                  (isMeasurement ? "g-data" : "")
                }
              >
                {value}
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

/**
 * Trust signals, all in one chip shape so no single one shouts. Every product
 * on this surface is from a verified seller, so "verified" is the baseline
 * rather than a distinction — the rating and the warranty are the facts that
 * actually vary. Colour is never the sole signal: each chip pairs icon + text.
 * Nothing is invented: the rating renders verbatim (never rounded) and only
 * when the seller has one — many sellers genuinely have zero reviews.
 */
function TrustSignals({
  seller,
  t,
}: {
  seller: ProductDetailSeller;
  t: (key: string, opts?: Record<string, unknown>) => string;
}): ReactElement {
  const rating =
    seller.rating !== null && String(seller.rating).trim() !== ""
      ? String(seller.rating).trim()
      : null;
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="product-detail-trust"
    >
      {seller.verified ? (
        <span className={TRUST_CHIP} aria-label={t("aria.verifiedSeller")}>
          <BadgeCheck
            {...iconProps("xs")}
            className="text-[color:var(--g-primary)]"
          />
          {t("trust.verifiedSeller")}
        </span>
      ) : null}
      {rating !== null ? (
        <span
          data-testid="product-detail-rating"
          aria-label={t("aria.rating", {
            rating,
            count: seller.reviews_count,
          })}
          className={TRUST_CHIP}
        >
          <Star {...iconProps("xs")} className="text-[color:var(--g-warn)]" />
          <span className="g-data">
            {t("trust.rating", { rating, count: seller.reviews_count })}
          </span>
        </span>
      ) : null}
      {seller.warranty ? (
        <span className={TRUST_CHIP} aria-label={t("aria.warranty")}>
          <ShieldCheck {...iconProps("xs")} />
          {t("trust.warranty")}
        </span>
      ) : null}
    </div>
  );
}

/** A skeleton shaped like the page it stands in for, not like a grey box. */
function DetailSkeleton(): ReactElement {
  return (
    <div
      className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,24rem)_minmax(0,1fr)] lg:items-start lg:gap-8"
      data-testid="product-detail-skeleton"
      aria-hidden="true"
    >
      <div className="g-skel aspect-[4/3] w-full rounded-[var(--g-radius)]" />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="g-skel h-6 w-4/5 rounded-full" />
          <div className="g-skel h-6 w-2/5 rounded-full" />
        </div>
        <div className="g-ruled overflow-hidden rounded-[var(--g-radius)] border border-[color:var(--g-rule)]">
          <div className="px-4 py-4">
            <div className="g-skel h-7 w-2/5 rounded-[4px]" />
          </div>
          <div className="flex flex-col gap-2 px-4 py-4">
            <div className="g-skel h-4 w-1/2 rounded-full" />
            <div className="g-skel h-3 w-2/3 rounded-full" />
            <div className="g-skel h-3 w-1/3 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDetailPage;
